import classnames from 'classnames';
import {
  forwardRef,
  HTMLAttributes,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { usePositionEffect } from '../../utils';

import styles from './TableHeader.module.scss';

export const TableHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const innerRef = useRef<HTMLDivElement>(null);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    useImperativeHandle(ref, () => innerRef.current!);
    const floatingRef = useRef<HTMLDivElement | null>(null);

    const [size, setSize] = useState<'small' | 'medium' | 'large'>('small');
    const [isFloating, setIsFloating] = useState(false);
    const [horizontalScrolling, setHorizontalScrolling] = useState(false);
    const [clientTop, setClientTop] = useState(0);
    const [top, setTop] = useState(0);
    const [bottom, setBottom] = useState(0);
    const [left, setLeft] = useState(0);
    const [right, setRight] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const setSizes = useCallback(() => {
      if (!innerRef.current) return;
      const isFloating = innerRef.current.matches(
        '[data-sticky-header]:not([data-vertical-scrolling]) *'
      );
      setIsFloating(isFloating);
      if (!isFloating) return;
      const table = innerRef.current.closest('[data-sticky-header]');
      if (!table) return;
      const boundingBox = table.getBoundingClientRect();
      setSize((table.getAttribute('data-table-size') ?? 'small') as 'small');
      setClientTop(boundingBox.top);
      setTop(parseInt(table.getAttribute('data-sticky-header-distance') ?? '0'));
      setHorizontalScrolling(!!table.getAttribute('data-horizontal-scrolling'));
      setLeft(boundingBox.left);
      setRight(document.body.clientWidth - boundingBox.right);
      setScrollLeft(table.scrollLeft);
      setBottom(boundingBox.bottom);
      if (!floatingRef.current) return;
      for (let row = 0; row < innerRef.current.children.length; row++) {
        if (floatingRef.current.children[row]) {
          for (let column = 0; column < innerRef.current.children[row].children.length; column++) {
            if (floatingRef.current.children[row].children[column]) {
              const width =
                innerRef.current.children[row].children[column].getBoundingClientRect().width;
              const th = floatingRef.current.children[row].children[column] as HTMLElement;
              th.style.setProperty('width', width + 'px', '');
              th.style.setProperty('min-width', width + 'px', '');
              th.style.setProperty('max-width', width + 'px', '');
            }
          }
        }
      }
    }, []);
    usePositionEffect(() => setSizes(), [setSizes], innerRef);
    useLayoutEffect(() => {
      if (!isFloating) return;
      const table = innerRef.current?.closest('[data-sticky-header]');
      if (!table) return;
      const controller = new AbortController();
      table.addEventListener('scroll', setSizes, { signal: controller.signal });
      return () => controller.abort();
    }, [isFloating, setSizes]);
    useLayoutEffect(() => {
      if (!floatingRef.current?.parentElement?.parentElement) return;
      floatingRef.current.parentElement.parentElement.scrollLeft = scrollLeft;
    });

    const showFloatingHeader = isFloating && clientTop < top;

    return (
      <>
        <div
          ref={innerRef}
          className={classnames(styles.container, className, {
            [styles.hidden]: showFloatingHeader,
          })}
          data-role="thead"
          {...props}
        />
        {showFloatingHeader &&
          createPortal(
            <div
              className={styles.floating}
              style={{ top: top + 'px', left: left + 'px', right: right + 'px' }}
            >
              <div
                data-role="table"
                style={{
                  translate:
                    '0 ' +
                    Math.min(bottom - top - (floatingRef.current?.clientHeight ?? 0), 0) +
                    'px',
                }}
                className={styles[size]}
                data-horizontal-scrolling={horizontalScrolling ? true : undefined}
              >
                <div
                  className={classnames(styles.container, className)}
                  data-role="thead"
                  data-floating-header
                  ref={floatingRef}
                  {...props}
                />
              </div>
            </div>,
            document.body
          )}
      </>
    );
  }
);
